import * as R from "ramda";

import MenuItem from "api/types/MenuItem";

import calculateMenuItemUnitPriceCents from "./calculateMenuItemUnitPriceCents";

/**
 * Calculates cart prices
 */
const calculateCart = <
	T extends {
		cartItems: {
			localId: string;
			menuItem: MenuItem;
			selectedOptions: {
				menuItemOptionId: string;
				menuItemOptionGroupId?: string;
			}[];
			quantity: number;
		}[];
	}
>(
	{ cartItems }: T,
	taxRate: number,
	tipCents: number,
	deliveryCents?: number,
	isDeliveryTaxable?: boolean,
	percentOff?: number
): {
	cartTotalCents: number;
	taxCents: null | number;
	grandTotalCents: number;
	cartItemByLocalId: {
		[localId: string]: T["cartItems"][number];
	};
	discount: number;
} => {
	let cartTotalCents = 0;

	for (const item of cartItems) {
		const unitPriceCents = calculateMenuItemUnitPriceCents(item);
		const extendedPriceCents = unitPriceCents * item.quantity;
		cartTotalCents += extendedPriceCents;
	}

	let taxCents = Math.round((taxRate / 100) * cartTotalCents);

	let grandTotalCents = cartTotalCents + taxCents + tipCents;

	if (deliveryCents) {
		grandTotalCents += deliveryCents;

		if (isDeliveryTaxable) {
			const deliveryTaxCents = Math.round((taxRate / 100) * deliveryCents);

			taxCents += deliveryTaxCents;
			grandTotalCents += deliveryTaxCents;
		}
	}

	let discount = 0;
	if (percentOff) {
		if (percentOff < 0 || percentOff > 100) throw new Error(`Invalid percentOff: ${percentOff}`);
		discount = Math.round(percentOff * 0.01 * cartTotalCents);
		grandTotalCents -= discount;
	}

	return {
		cartTotalCents,
		taxCents,
		grandTotalCents,
		cartItemByLocalId: R.fromPairs(cartItems.map(x => [x.localId, x])),
		discount
	};
};

export default calculateCart;
